import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

const fmt = (n) => Number(n || 0).toLocaleString();

// Favorite actions belong to the project (shared by every member), not to the account.
export function useActionFavorites(projectId, fetchJson) {
  const [favorites, setFavorites] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState('');
  // Project whose favorites are loaded; the picker picks its starting view from it.
  const [loadedFor, setLoadedFor] = useState('');

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    setFavorites([]);
    setError('');
    setLoadedFor('');
    fetchJson(`/api/analytics/projects/${projectId}/funnel/favorites`)
      .then((data) => {
        if (cancelled) return;
        setFavorites(Array.isArray(data.favorites) ? data.favorites : []);
        setCanEdit(Boolean(data.canEdit));
      })
      .catch(() => {
        if (!cancelled) setCanEdit(false);
      })
      .finally(() => {
        if (!cancelled) setLoadedFor(projectId);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, fetchJson]);

  const toggle = useCallback(async (action) => {
    const isFavorite = favorites.includes(action);
    const previous = favorites;
    setFavorites(isFavorite ? favorites.filter((a) => a !== action) : [...favorites, action]);
    setError('');
    try {
      const data = await fetchJson(`/api/analytics/projects/${projectId}/funnel/favorites`, {
        method: 'PUT',
        body: JSON.stringify({ action, favorite: !isFavorite }),
      });
      if (Array.isArray(data.favorites)) setFavorites(data.favorites);
    } catch (err) {
      setFavorites(previous);
      setError(err.message || 'Could not save the favorite.');
    }
  }, [favorites, fetchJson, projectId]);

  return { favorites, canEdit, error, loadedFor, toggle };
}

// "★ Favorites / All actions" list with search, shared by Funnels and Heatmaps.
// Both views live in one fixed-height list, so starring never pushes the page down.
// `multi` shows checkboxes (funnel steps); otherwise a radio dot (one heatmap action).
const ActionPicker = ({ actions, favorites: fav, isSelected, onPick, multi = false, onAddAll, height = '16.375rem' }) => {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  // Rows shown in Favorites since it was opened: unstarring keeps the row (dimmed,
  // empty star) until the view changes, so nothing moves under the cursor.
  const [shown, setShown] = useState([]);

  // Open on Favorites when the project has some, otherwise on All actions.
  useEffect(() => {
    if (!fav.loadedFor) return;
    setShown(fav.favorites);
    setTab(fav.favorites.length > 0 ? 'favorites' : 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fav.loadedFor]);

  const byName = new Map(actions.map((a) => [a.action, a]));
  const toOptions = (names) => names.map((name) => byName.get(name)).filter(Boolean);
  const favoriteOptions = toOptions(fav.favorites);
  const favoriteSet = new Set(fav.favorites);
  const showingFavorites = tab === 'favorites';
  const query = search.trim().toLowerCase();
  // "All actions" lists every action, favorites included (with a filled star).
  const options = (showingFavorites ? toOptions([...new Set([...shown, ...fav.favorites])]) : actions)
    .filter((a) => !query || a.action.toLowerCase().includes(query));

  const openTab = (next) => {
    setShown(fav.favorites);
    setTab(next);
  };

  return (
    <>
      <Segmented role="tablist" aria-label="Action list">
        {[['favorites', '★ Favorites', favoriteOptions.length], ['all', 'All actions', actions.length]].map(([id, label, count]) => (
          <Segment key={id} type="button" role="tab" aria-selected={tab === id} $on={tab === id} onClick={() => openTab(id)}>
            {label}
            <SegmentCount $on={tab === id}>{count}</SegmentCount>
          </Segment>
        ))}
      </Segmented>

      <SearchRow>
        <SearchInput
          type="search"
          aria-label="Search actions"
          placeholder={showingFavorites ? 'Search favorites' : 'Search actions'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {onAddAll && (
          <AddAllButton
            type="button"
            onClick={() => onAddAll(favoriteOptions.map((a) => a.action))}
            disabled={favoriteOptions.length === 0}
            title="Select every favorite"
          >
            <span aria-hidden="true" style={{ color: 'var(--brand)' }}>★</span> Add all
          </AddAllButton>
        )}
      </SearchRow>

      <RowList style={{ height }}>
        {options.length > 0 ? options.map((a) => {
          const on = isSelected(a.action);
          const starred = favoriteSet.has(a.action);
          return (
            <ActionRow key={a.action}>
              <ActionToggle
                type="button"
                $on={on}
                $dim={showingFavorites && !starred}
                aria-pressed={on}
                onClick={() => onPick(a.action)}
              >
                <Box $on={on} $round={!multi} aria-hidden="true">{multi && on ? '✓' : ''}</Box>
                <ActionLabel>{a.action}</ActionLabel>
                <Count>{fmt(a.players)}</Count>
              </ActionToggle>
              <StarButton
                type="button"
                $on={starred}
                aria-pressed={starred}
                aria-label={starred ? `Remove ${a.action} from favorites` : `Add ${a.action} to favorites`}
                title={fav.canEdit ? (starred ? 'Remove from favorites' : 'Add to favorites') : 'Only workspace owners and admins can change favorites'}
                disabled={!fav.canEdit}
                onClick={() => fav.toggle(a.action)}
              >
                {starred ? '★' : '☆'}
              </StarButton>
            </ActionRow>
          );
        }) : query ? (
          <EmptyRow>No actions match your search.</EmptyRow>
        ) : (
          <EmptyRow>
            <EmptyStar aria-hidden="true">☆</EmptyStar>
            {fav.canEdit
              ? 'No favorites yet. Star an action in All actions and it shows up here for the whole team.'
              : 'No favorites yet. Workspace owners and admins can star actions to pin them here.'}
          </EmptyRow>
        )}
      </RowList>
      {fav.error && <ErrorNote>{fav.error}</ErrorNote>}
    </>
  );
};

export default ActionPicker;

const Segmented = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.25rem;
  margin-bottom: 0.625rem;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.625rem;
`;

const Segment = styled.button`
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2rem;
  border-radius: 0.4375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => (p.$on ? '#23262d' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--text-primary)' : 'var(--text-secondary)')};
  box-shadow: ${(p) => (p.$on ? 'inset 0 1px 0 rgba(255, 255, 255, 0.04)' : 'none')};

  &:hover {
    color: var(--text-primary);
  }
`;

const SegmentCount = styled.span`
  padding: 1px 0.375rem;
  border-radius: 999px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${(p) => (p.$on ? 'rgba(255, 122, 46, 0.16)' : '#15181d')};
  color: ${(p) => (p.$on ? 'var(--brand)' : 'var(--text-tertiary)')};
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  min-width: 0;
  min-height: 2.375rem;
  padding: 0.55rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-accent);
  background: var(--background);
  color: var(--text-primary);
  font-size: 0.875rem;

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const AddAllButton = styled.button`
  flex-shrink: 0;
  min-height: 2.375rem;
  padding: 0 0.75rem;
  background: transparent;
  border: 1px solid var(--border-accent);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: var(--text-tertiary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const RowList = styled.ul`
  list-style: none;
  overflow-y: auto;
`;

const EmptyRow = styled.li`
  padding: 2.5rem 1rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--text-tertiary);
`;

const EmptyStar = styled.div`
  font-size: 1.375rem;
  color: var(--border-accent);
  margin-bottom: 0.375rem;
`;

const ActionRow = styled.li`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ActionToggle = styled.button`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 2.375rem;
  padding: 0 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  text-align: left;
  color: ${(p) => (p.$dim ? 'var(--text-tertiary)' : 'var(--text-primary)')};
  background: ${(p) => (p.$on ? 'rgba(255, 122, 46, 0.1)' : 'transparent')};

  &:hover {
    background: ${(p) => (p.$on ? 'rgba(255, 122, 46, 0.14)' : '#15181d')};
  }
`;

const Box = styled.span`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(p) => (p.$round ? '50%' : '0.25rem')};
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--brand-ink);
  border: 1px solid ${(p) => (p.$on ? 'var(--brand)' : 'var(--border-accent)')};
  background: ${(p) => (p.$on ? 'var(--brand)' : 'var(--background)')};
  /* Radio look: a dark ring inside the filled dot. */
  box-shadow: ${(p) => (p.$round && p.$on ? 'inset 0 0 0 3px var(--background)' : 'none')};
`;

const ActionLabel = styled.span`
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Count = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  color: var(--text-tertiary);
`;

const StarButton = styled.button`
  width: 2.125rem;
  height: 2.125rem;
  flex-shrink: 0;
  border-radius: 0.375rem;
  background: transparent;
  font-size: 1rem;
  color: ${(p) => (p.$on ? 'var(--brand)' : '#4a4f58')};

  &:hover:not(:disabled) {
    color: var(--brand);
    background: #15181d;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const ErrorNote = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(240, 82, 82, 0.08);
  color: var(--error);
  font-size: 0.875rem;
`;
